import moment from 'moment'
import _ from 'lodash'
import actions from './actions'
import { PlanningAssistantState, PlanningState } from '@/types/application-state'
import * as getterTypes from '@/store/getter-types'
import * as mutationTypes from '@/store/mutation-types'
import { ShiftEditorPlanningPayload } from '@/model/planning/ShiftEditorPlanningPayload'
import { AdditionalFieldsPayloadItem, CompanyFilterValue, PlanningAssistantPeriodicityPayload } from '@/types/payload'
import { ShiftTemplate } from '@/model/planning/ShiftTemplate'
import { EmployeeAvailableForPlanning } from '@/model/planning/EmployeeAvailableForPlanning'
import { Employee } from '@/model/companyStructure/Employee'
import { PlannedEmployee } from '@/model/planning/PlannedEmployee'
import { Task } from '@/model/planning/Task'
import { ShiftAvailability } from '@/types/enums'
import { AutomaticallyPlannedEmployee } from '@/model/planning/AutomaticallyPlannedEmployee'
import { EmployeePlaceholder } from '@/model/planning/EmployeePlaceholder'
import { PlannedShift } from '@/model/planning/PlannedShift'
import { PeriodicityPlannedEmployee } from '@/types'
import { AdditionalGroup } from '@/model/additional-fields/AdditionalGroup'

const defaultPlanningAssistantState = (): PlanningAssistantState => {
  const startDate = moment().startOf('day').add(8, 'hour')
  const endDate = moment(startDate).add(8, 'hour')

  return {
    initialEmployeeList: [],

    companyStructure: {
      profitCenterList: [],
      departmentList: [],
      planningGroupList: [],
      employeeList: []
    },

    filterValue: {
      profitCenter: undefined,
      department: undefined,
      planningGroup: undefined,
      employee: undefined
    },

    shiftTemplateItem: undefined,
    startDate: startDate.toDate(),
    endDate: endDate.toDate(),

    taskList: [],
    employeeForPlaningList: [],
    filteredEmployeesByDepartment: [],

    periodicity: {
      isUnique: true,
      weekDayList: [],
      repeatEveryNthWeek: 1,
      expireAfterRepetitions: 0,
      expireAfterDate: undefined
    },

    plannedEmployeeList: [],
    plannedEmployeeExchange: undefined,
    plannedEmployeeDeletion: undefined,
    shiftAvailability: ShiftAvailability.Temporary,
    currentShift: undefined,
    automaticPlanningId: undefined,
    automaticPlanningIsProject: true,
    isEdittingSerie: false,
    freePeriodicityPlanningId: undefined,
    periodicityPlannedEmployeeList: [],
    additionalFieldsList: [],
    additionalFields: []
  }
}

const state: PlanningState = {
  planningAssistant: defaultPlanningAssistantState()
}

const mutations = {
  //<editor-fold desc="Planning Assistant Mutations">
  [mutationTypes.PLANNING_ASSISTANT_RESET_STATE_MUTATION] (state: PlanningState): void {
    Object.assign(state.planningAssistant, defaultPlanningAssistantState())
  },

  [mutationTypes.PLANNING_ASSISTANT_SET_DATA_FROM_PAYLOAD_MUTATION] (state: PlanningState, payload: ShiftEditorPlanningPayload): void {
    state.planningAssistant.initialEmployeeList = payload.employeeList
    state.planningAssistant.companyStructure = payload.companyStructure
    state.planningAssistant.filterValue = payload.filterValue

    if (payload.eventTimePeriod) {
      state.planningAssistant.startDate = payload.eventTimePeriod.start
      state.planningAssistant.endDate = payload.eventTimePeriod.end
    }
  },

  [mutationTypes.PLANNING_ASSISTANT_SET_START_DATE_MUTATION] (state: PlanningState, payload: Date): void {
    state.planningAssistant.startDate = payload
  },

  [mutationTypes.PLANNING_ASSISTANT_SET_END_DATE_MUTATION] (state: PlanningState, payload: Date): void {
    state.planningAssistant.endDate = payload
  },

  [mutationTypes.PLANNING_ASSISTANT_SET_SHIFT_TEMPLATE_ITEM_MUTATION] (state: PlanningState, payload: ShiftTemplate | undefined): void {
    state.planningAssistant.shiftTemplateItem = payload
  },

  [mutationTypes.PLANNING_ASSISTANT_SET_PERIODICITY_MUTATION] (state: PlanningState, payload: PlanningAssistantPeriodicityPayload): void {
    state.planningAssistant.periodicity = _.cloneDeep(payload)
  },

  [mutationTypes.PLANNING_ASSISTANT_SET_EMPLOYEE_LIST_FOR_PLANNING_MUTATION] (state: PlanningState, payload: EmployeeAvailableForPlanning[]): void {
    state.planningAssistant.employeeForPlaningList = _.cloneDeep(payload)
  },

  [mutationTypes.PLANNING_ASSISTANT_SET_PLANNED_EMPLOYEE_LIST_MUTATION] (state: PlanningState, payload: PlannedEmployee[]): void {
    state.planningAssistant.plannedEmployeeList = _.cloneDeep(payload)
  },

  [mutationTypes.PLANNING_ASSISTANT_SET_TASK_LIST_MUTATION] (state: PlanningState, payload: Task[]): void {
    state.planningAssistant.taskList = _.cloneDeep(payload)
  },

  [mutationTypes.PLANNING_ASSISTANT_SET_SHIFT_AVAILABILITY_MUTATION] (state: PlanningState, payload: ShiftAvailability): void {
    state.planningAssistant.shiftAvailability = payload
  },

  [mutationTypes.PLANNING_ASSISTANT_SET_EMPLOYEE_FOR_EXCHANGE_MUTATION] (state: PlanningState, payload: AutomaticallyPlannedEmployee|EmployeePlaceholder): void {
    state.planningAssistant.plannedEmployeeExchange = _.cloneDeep(payload)
  },

  [mutationTypes.PLANNING_ASSISTANT_DELETE_EMPLOYEE_FOR_EXCHANGE_MUTATION] (state: PlanningState, payload: AutomaticallyPlannedEmployee): void {
    state.planningAssistant.plannedEmployeeDeletion = _.cloneDeep(payload)
  },

  [mutationTypes.PLANNING_ASSISTANT_SET_CURRENT_SHIFT_MUTATION] (state: PlanningState, payload: PlannedShift): void {
    state.planningAssistant.currentShift = _.cloneDeep(payload)
  },

  [mutationTypes.PLANNING_ASSISTANT_SET_AUTOMATIC_PLANNING_ID_MUTATION] (state: PlanningState, payload: number | undefined): void {
    state.planningAssistant.automaticPlanningId = _.cloneDeep(payload)
  },

  [mutationTypes.PLANNING_ASSISTANT_SET_AUTOMATIC_PLANNING_IS_PROJECT_MUTATION] (state: PlanningState, payload: boolean): void {
    state.planningAssistant.automaticPlanningIsProject = _.cloneDeep(payload)
  },

  [mutationTypes.PLANNING_ASSISTANT_SET_FILTERED_EMPLOYEES_BY_DEPARTMENT_MUTATION] (state: PlanningState, payload: Employee[]): void {
    state.planningAssistant.filteredEmployeesByDepartment = _.cloneDeep(payload)
  },

  [mutationTypes.PLANNING_ASSISTANT_SET_IS_EDITTING_SERIE_MUTATION] (state: PlanningState, payload: boolean): void {
    state.planningAssistant.isEdittingSerie = _.cloneDeep(payload)
  },

  [mutationTypes.PLANNING_ASSISTANT_SET_FREE_PERIODICITY_PLANNING_ID_MUTATION] (state: PlanningState, payload: number | undefined): void {
    state.planningAssistant.freePeriodicityPlanningId = _.cloneDeep(payload)
  },

  [mutationTypes.PLANNING_ASSISTANT_SET_PERIODICITY_PLANNED_EMPLOYEE_LIST_MUTATION] (state: PlanningState, payload: PeriodicityPlannedEmployee[]): void {
    state.planningAssistant.periodicityPlannedEmployeeList = _.cloneDeep(payload)
  },

  [mutationTypes.PLANNING_ASSISTANT_SET_ADDITIONAL_FIELDS_LIST_MUTATION] (state: PlanningState, payload: AdditionalGroup[]): void {
    state.planningAssistant.additionalFieldsList = _.cloneDeep(payload)
  },

  [mutationTypes.PLANNING_ASSISTANT_SET_ADDITIONAL_FIELDS_MUTATION] (state: PlanningState, payload: AdditionalFieldsPayloadItem[]): void {
    state.planningAssistant.additionalFields = _.cloneDeep(payload)
  },

  //</editor-fold>
}

const getters = {
  //<editor-fold desc="Planning Assistant Getters">
  [getterTypes.PLANNING_ASSISTANT_GET_INITIAL_EMPLOYEE_LIST] (state: PlanningState): Employee[] {
    return state.planningAssistant.initialEmployeeList
  },

  [getterTypes.PLANNING_ASSISTANT_GET_START_DATE] (state: PlanningState): Date {
    return state.planningAssistant.startDate
  },

  [getterTypes.PLANNING_ASSISTANT_GET_END_DATE] (state: PlanningState): Date {
    return state.planningAssistant.endDate
  },

  [getterTypes.PLANNING_ASSISTANT_GET_COMPANY_FILTER] (state: PlanningState): CompanyFilterValue {
    return state.planningAssistant.filterValue
  },

  [getterTypes.PLANNING_ASSISTANT_GET_PERIODICITY] (state: PlanningState): PlanningAssistantPeriodicityPayload {
    return state.planningAssistant.periodicity
  },

  [getterTypes.PLANNING_ASSISTANT_GET_PLANNED_EMPLOYEE_LIST] (state: PlanningState): PlannedEmployee[] {
    return state.planningAssistant.plannedEmployeeList
  },

  [getterTypes.PLANNING_ASSISTANT_GET_EMPLOYEE_LIST_FOR_PLANNING] (state: PlanningState): EmployeeAvailableForPlanning[] {
    return state.planningAssistant.employeeForPlaningList
  },

  [getterTypes.PLANNING_ASSISTANT_GET_SHIFT_TEMPLATE_ITEM] (state: PlanningState): ShiftTemplate | undefined {
    return state.planningAssistant.shiftTemplateItem
  },

  [getterTypes.PLANNING_ASSISTANT_GET_TASK_LIST] (state: PlanningState): Task[] {
    return state.planningAssistant.taskList
  },

  [getterTypes.PLANNING_ASSISTANT_GET_SHIFT_AVAILABILITY] (state: PlanningState): ShiftAvailability {
    return state.planningAssistant.shiftAvailability
  },

  [getterTypes.PLANNING_ASSISTANT_GET_EMPLOYEE_FOR_EXCHANGE] (state: PlanningState): AutomaticallyPlannedEmployee | EmployeePlaceholder | undefined {
    return state.planningAssistant.plannedEmployeeExchange
  },

  [getterTypes.PLANNING_ASSISTANT_GET_EMPLOYEE_FOR_DELETION] (state: PlanningState): AutomaticallyPlannedEmployee | EmployeePlaceholder | undefined {
    return state.planningAssistant.plannedEmployeeDeletion
  },

  [getterTypes.PLANNING_ASSISTANT_GET_CURRENT_SHIFT] (state: PlanningState): PlannedShift | undefined {
    return state.planningAssistant.currentShift
  },

  [getterTypes.PLANNING_ASSISTANT_GET_AUTOMATIC_PLANNING_ID] (state: PlanningState): number | undefined {
    return state.planningAssistant.automaticPlanningId
  },

  [getterTypes.PLANNING_ASSISTANT_GET_AUTOMATIC_PLANNING_IS_PROJECT] (state: PlanningState): boolean {
    return state.planningAssistant.automaticPlanningIsProject
  },

  [getterTypes.PLANNING_ASSISTANT_GET_FILTERED_EMPLOYEES_BY_DEPARTMENT] (state: PlanningState): Employee[] {
    return state.planningAssistant.filteredEmployeesByDepartment
  },

  [getterTypes.PLANNING_ASSISTANT_GET_IS_EDITTING_SERIE] (state: PlanningState): boolean {
    return state.planningAssistant.isEdittingSerie
  },

  [getterTypes.PLANNING_ASSISTANT_GET_FREE_PERIODICITY_PLANNING_ID] (state: PlanningState): number | undefined {
    return state.planningAssistant.freePeriodicityPlanningId
  },

  [getterTypes.PLANNING_ASSISTANT_GET_PERIODICITY_PLANNED_EMPLOYEE_LIST] (state: PlanningState): PeriodicityPlannedEmployee[] {
    return state.planningAssistant.periodicityPlannedEmployeeList
  },

  [getterTypes.PLANNING_ASSISTANT_GET_ADDITIONAL_FIELDS_LIST] (state: PlanningState): AdditionalGroup[] {
    return state.planningAssistant.additionalFieldsList
  },

  [getterTypes.PLANNING_ASSISTANT_GET_ADDITIONAL_FIELDS] (state: PlanningState): AdditionalFieldsPayloadItem[] {
    return state.planningAssistant.additionalFields
  },

  //</editor-fold>
}

const planningModule = {
  state,
  getters,
  mutations,
  actions
}

export default planningModule
